import { Star, Search, ChevronDown, ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown } from "lucide-react";
import { useProduct } from "@/components/pdp/PdpDataContext";

/**
 * Visual replica of the real Judge.me widget that ships on the live
 * Riversol PDP. Content is a verbatim static copy of a handful of real
 * reviews pulled from the Anti-Aging Trio with Eye Repair page — the
 * demo needs to *look* like the shipping widget without spinning up the
 * Judge.me SDK at runtime. All copy, dates, ratings and photo thumbnails
 * live on `product.reviews.judgeme`.
 *
 * Rendering intentionally matches Judge.me's stock light theme: dark
 * `#0f0f0f`-ish stars, thin neutral dividers, small mono meta rows, and
 * a pill pagination footer.
 */
const JudgemeStars = ({ n = 5, size = 12 }) => (
  <div className="flex gap-[2px]">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={size}
        className={i < n ? "fill-neutral-900 text-neutral-900" : "text-neutral-300"}
      />
    ))}
  </div>
);

const Avatar = ({ author }) => {
  const initial = (author || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-neutral-200 text-neutral-500 text-sm font-medium flex items-center justify-center shrink-0">
      {initial}
    </div>
  );
};

const VerifiedBadge = ({ badge }) => {
  if (badge === "shop") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-neutral-500">
        <span className="inline-block w-3 h-3 rounded-full bg-violet-600" aria-hidden />
        <span>Verified by Shop</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center border border-neutral-300 rounded-full px-2 py-0.5 text-[11px] text-neutral-700">
      Verified Buyer *
    </span>
  );
};

const ReviewItem = ({ r }) => (
  <div className="border-t border-neutral-200 py-6" data-testid={`judgeme-review-${r.author.replace(/\W+/g, "-").toLowerCase()}`}>
    <div className="mb-3">
      <JudgemeStars n={r.rating} size={13} />
    </div>
    <div className="flex items-start gap-3 mb-3">
      <Avatar author={r.author} />
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-medium text-neutral-900 text-[14px]">{r.author}</span>
          <VerifiedBadge badge={r.badge} />
        </div>
        <div className="text-[12.5px] text-neutral-500 mt-0.5">{r.date}</div>
      </div>
    </div>
    {r.title && (
      <div className="font-semibold text-neutral-900 text-[14.5px] mb-1">{r.title}</div>
    )}
    <p className="text-[14px] leading-relaxed text-neutral-700">{r.body}</p>
    {r.source && (
      <div className="text-[12.5px] text-neutral-500 mt-2">{r.source}</div>
    )}
    {r.reply && (
      <div className="mt-4 border-l-2 border-neutral-200 pl-4 py-1">
        <div className="text-[13px] text-neutral-500 mb-1">Reply from Riversol:</div>
        <p className="text-[14px] leading-relaxed text-neutral-700">{r.reply}</p>
      </div>
    )}
    <div className="mt-4 flex items-center gap-4 text-[13px] text-neutral-500">
      <span>Helpful?</span>
      <button type="button" className="inline-flex items-center gap-1.5 hover:text-neutral-900 transition-colors">
        <ThumbsUp size={14} /> 0
      </button>
      <button type="button" className="inline-flex items-center gap-1.5 hover:text-neutral-900 transition-colors">
        <ThumbsDown size={14} /> 0
      </button>
    </div>
  </div>
);

export const JudgemeReplica = ({ data, rating }) => {
  const isV32 = !!useProduct().v32;
  const { total, questionsTotal, pages, photos, items } = data;
  const totalStr = total.toLocaleString();

  return (
    <div
      className="bg-white border border-neutral-200 px-6 lg:px-10 py-8 lg:py-12"
      data-testid="judgeme-replica"
    >
      {/* HEADER */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
        <div>
          <h3 className="text-[22px] lg:text-[26px] font-medium text-neutral-900 mb-3">
            Customer Reviews
          </h3>
          <div className="flex items-center gap-2">
            <Star size={18} className="fill-neutral-900 text-neutral-900" />
            <span className="text-[22px] font-medium text-neutral-900 leading-none">
              {rating}
            </span>
            <span className="text-[15px] text-neutral-600 ml-2">
              {totalStr} reviews
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className={`bg-neutral-900 text-white text-[13px] tracking-wide font-medium px-6 py-3 ${isV32 ? "" : "rounded-full"} hover:bg-neutral-800 transition-colors`}
            data-testid="judgeme-write-review"
          >
            Write A Review
          </button>
          <button
            type="button"
            className="border border-neutral-300 text-neutral-800 text-[13px] tracking-wide font-medium px-6 py-3 rounded-full hover:border-neutral-500 transition-colors"
            data-testid="judgeme-ask-question"
          >
            Ask A Question
          </button>
        </div>
      </div>

      {/* PHOTO STRIP */}
      <div
        className="flex gap-2 overflow-x-auto no-scrollbar pb-4 mb-2 -mx-2 px-2"
        data-testid="judgeme-photos"
      >
        {photos.map((src, i) => (
          <div
            key={i}
            className="shrink-0 w-16 h-16 lg:w-[70px] lg:h-[70px] rounded overflow-hidden bg-neutral-100"
          >
            <img
              src={src}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* TABS + SORT */}
      <div className="flex items-center justify-between gap-4 border-b border-neutral-200 mt-4">
        <div className="flex gap-6">
          <button
            type="button"
            className="pb-3 text-[14px] font-medium text-neutral-900 border-b-2 border-neutral-900 -mb-px"
          >
            Reviews ({totalStr})
          </button>
          <button
            type="button"
            className="pb-3 text-[14px] font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            Question ({questionsTotal})
          </button>
        </div>
        <div className="flex items-center gap-3 pb-2">
          <button
            type="button"
            aria-label="Search reviews"
            className="w-8 h-8 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-600 hover:border-neutral-500 transition-colors"
          >
            <Search size={14} />
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 border border-neutral-300 rounded-full px-4 py-1.5 text-[13px] text-neutral-700 hover:border-neutral-500 transition-colors"
          >
            Highest rating
            <ChevronDown size={14} />
          </button>
        </div>
      </div>

      {/* REVIEW LIST */}
      <div>
        {items.map((r, i) => (
          <ReviewItem key={i} r={r} />
        ))}
      </div>

      {/* PAGINATION */}
      <div
        className="border-t border-neutral-200 pt-8 flex items-center justify-center gap-2"
        data-testid="judgeme-pagination"
      >
        <button
          type="button"
          aria-label="Previous page"
          className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type="button"
          aria-current="page"
          className="w-8 h-8 rounded-full bg-neutral-900 text-white text-[13px] font-medium flex items-center justify-center"
        >
          1
        </button>
        {[2, 3, 4].map((n) => (
          <button
            key={n}
            type="button"
            className="w-8 h-8 rounded-full text-neutral-700 text-[13px] font-medium hover:bg-neutral-100 transition-colors"
          >
            {n}
          </button>
        ))}
        <span className="text-neutral-500 px-1">…</span>
        <button
          type="button"
          className="w-10 h-8 rounded-full text-neutral-700 text-[13px] font-medium hover:bg-neutral-100 transition-colors"
        >
          {pages}
        </button>
        <button
          type="button"
          aria-label="Next page"
          className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default JudgemeReplica;
